import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { CheckCircle, XCircle, HelpCircle, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';

/**
 * FillInBlank — fill in the blank exercise
 *
 * phase.content schema (backend resolved):
 * {
 *   sentences: [{ text: string, answer: string, hint: string|null }],
 *   points_per_sentence: number
 * }
 *
 * `text` contains `___` as the blank marker.
 * `answer` is the correct answer (resolved string, no i18n dict).
 *
 * Legacy fallback: questions[] array with sentence_tr/correct_answer_tr/alternatives_tr
 */

const BLANK_MARKER = '___';

function normalizeAnswer(str) {
  return str.trim().toLowerCase().replace(/[.,;:!?]/g, '');
}

function parseSegments(sentence) {
  const parts = sentence.split(BLANK_MARKER);
  const segments = [];
  parts.forEach((part, i) => {
    if (part) segments.push({ type: 'text', value: part });
    if (i < parts.length - 1) {
      segments.push({ type: 'blank', index: i });
    }
  });
  return segments;
}

export default function FillInBlank({
  phase,
  onPhaseComplete,
  checkAnswer,
  'data-testid': testId = 'fill-in-blank',
}) {
  const { t, lang } = useLanguage();
  const { play } = useSoundEffects();
  const shouldReduce = useReducedMotion();

  /**
   * Normalize data source: support both backend `sentences` schema and
   * legacy `questions` schema.
   *
   * Backend: sentences[i] = { text, answer, hint }
   * Legacy:  questions[i] = { id, sentence_tr, sentence_en, correct_answer_tr,
   *                           correct_answer_en, alternatives_tr, alternatives_en,
   *                           word_pool_tr, word_pool_en, mode }
   */
  const rawSentences = phase?.content?.sentences;
  const rawQuestions = phase?.content?.questions;

  const questions = React.useMemo(() => {
    if (Array.isArray(rawSentences) && rawSentences.length > 0) {
      // Backend schema → normalise into unified shape
      return rawSentences.map((s, i) => ({
        _id: i, // stable index key (backend doesn't send id)
        sentence: s.text || '',
        correctAnswer: s.answer || '',
        alternatives: [],
        hint: s.hint || null,
        mode: 'text_input',
        wordPool: [],
      }));
    }
    if (Array.isArray(rawQuestions) && rawQuestions.length > 0) {
      // Legacy schema
      return rawQuestions.map((q, i) => {
        const sentenceKey = lang === 'tr' ? 'sentence_tr' : 'sentence_en';
        const correctKey = lang === 'tr' ? 'correct_answer_tr' : 'correct_answer_en';
        const altKey = lang === 'tr' ? 'alternatives_tr' : 'alternatives_en';
        const poolKey = lang === 'tr' ? 'word_pool_tr' : 'word_pool_en';
        return {
          _id: q.id || i,
          sentence: q[sentenceKey] || q.sentence_tr || '',
          correctAnswer: q[correctKey] || q.correct_answer_tr || '',
          alternatives: q[altKey] || q.alternatives_tr || [],
          hint: q.hint_tr || q.hint || null,
          mode: q.mode || 'text_input',
          wordPool: q[poolKey] || q.word_pool_tr || [],
        };
      });
    }
    return [];
  }, [rawSentences, rawQuestions, lang]);

  const maxXP = phase?.xp_in_phase || 0;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const inputRef = useRef(null);
  // Server-side grading (Bug 3)
  const collectedAnswers = useRef([]);
  const [serverResult, setServerResult] = useState(null);
  // checking: true while awaiting server check-answer response (suppress color flash)
  const [checking, setChecking] = useState(false);

  const currentQ = questions[currentIndex];
  const isLast = currentIndex === questions.length - 1;

  // All useCallbacks must be called unconditionally (before any early return)
  const isAnswerCorrect = useCallback(
    (userAnswer) => {
      if (!currentQ) return false;
      const norm = normalizeAnswer(userAnswer);
      if (norm === normalizeAnswer(currentQ.correctAnswer)) return true;
      return currentQ.alternatives.some((alt) => normalizeAnswer(alt) === norm);
    },
    [currentQ]
  );

  const handleTextInput = useCallback(
    (e) => {
      if (submitted || !currentQ) return;
      setInputValue(e.target.value);
    },
    [submitted, currentQ]
  );

  const handleWordSelect = useCallback(
    (word) => {
      if (submitted || !currentQ) return;
      play('click');
      setInputValue(word);
    },
    [submitted, currentQ, play]
  );

  const handleHint = useCallback(() => {
    if (submitted || hintUsed || !currentQ) return;
    setHintUsed(true);
    play('click');
    // Show first 2 chars of answer as hint
    const hint = currentQ.correctAnswer.slice(0, 2) + '...';
    setInputValue(hint);
  }, [submitted, hintUsed, currentQ, play]);

  const handleSubmit = useCallback(async () => {
    if (!inputValue.trim() || submitted) return;
    setSubmitted(true);

    collectedAnswers.current = [
      ...collectedAnswers.current,
      { question_index: currentIndex, text: inputValue },
    ];

    let correct;
    if (typeof checkAnswer === 'function') {
      // Renk flash'ini onlemek icin server yaniti gelene kadar checking=true tut
      setChecking(true);
      const res = await checkAnswer({
        phaseId: phase?.phase_id,
        questionIndex: currentIndex,
        answer: { text: inputValue },
      });
      setChecking(false);
      if (res) {
        setServerResult(res);
        correct = !!res.is_correct;
      } else {
        correct = isAnswerCorrect(inputValue);
      }
    } else {
      correct = isAnswerCorrect(inputValue);
    }

    if (correct) {
      play('correct');
      setCorrectCount((c) => c + 1);
    } else {
      play('wrong');
    }
  }, [inputValue, submitted, currentIndex, checkAnswer, phase, isAnswerCorrect, play]);

  const handleNext = useCallback(() => {
    if (isLast) {
      const score =
        questions.length > 0
          ? Math.round((correctCount / questions.length) * maxXP)
          : 0;
      onPhaseComplete({
        score,
        maxScore: maxXP,
        answers: collectedAnswers.current, // [{question_index, text}]
        timeSpent: 0,
      });
    } else {
      setCurrentIndex((i) => i + 1);
      setInputValue('');
      setSubmitted(false);
      setHintUsed(false);
      setServerResult(null);
      setChecking(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLast, correctCount, questions.length, maxXP, onPhaseComplete]);

  // Early return after all hooks
  if (!currentQ) return null;

  const isWordPool = currentQ.mode === 'word_pool' && currentQ.wordPool.length > 0;
  const segments = parseSegments(currentQ.sentence);
  // checking=true means we're awaiting server; don't apply color until server responds
  const correct = submitted && !checking && (serverResult ? !!serverResult.is_correct : isAnswerCorrect(inputValue));
  const wrong = submitted && !checking && !correct;
  // Server modunda dogru cevap server'dan gelir (strip nedeniyle currentQ.correctAnswer bos)
  const displayCorrectAnswer =
    serverResult && serverResult.correct_answer != null
      ? serverResult.correct_answer
      : currentQ.correctAnswer;

  // Show hint if backend provides one (resolved string) or compute from answer
  const hintDisplay = currentQ.hint || null;

  return (
    <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto" data-testid={testId}>
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
        <span data-testid={`${testId}-progress`}>
          {(t('exercise.question_counter') || 'Soru {current} / {total}')
            .replace('{current}', currentIndex + 1)
            .replace('{total}', questions.length)}
        </span>
        {maxXP > 0 && (
          <span className="font-semibold text-amber-500">
            {(t('exercise.max_xp') || 'Maks. {xp} XP')
              .replace('{xp}', maxXP)}
          </span>
        )}
      </div>

      {/* Sentence with blank */}
      <motion.div
        key={currentIndex}
        initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="text-lg font-medium text-[var(--text-main)] leading-loose flex flex-wrap items-center gap-1"
        data-testid={`${testId}-sentence`}
      >
        {segments.map((seg, idx) => {
          if (seg.type === 'text') {
            return <span key={idx}>{seg.value}</span>;
          }

          const borderColor = submitted
            ? correct
              ? 'border-[#10B981]'
              : 'border-[var(--brand-danger)]'
            : 'border-[var(--brand-primary)]';

          return (
            <span
              key={idx}
              className={`inline-flex items-center min-w-[80px] px-2 py-0.5 border-b-2 ${borderColor} font-bold ${
                correct
                  ? 'text-emerald-700'
                  : wrong
                  ? 'text-red-700'
                  : 'text-[var(--brand-primary)]'
              }`}
              aria-label={t('exercise.blank_label') || 'Boşluk'}
            >
              {inputValue || (
                <span className="text-[var(--text-muted)] font-normal text-sm">___</span>
              )}
            </span>
          );
        })}
      </motion.div>

      {/* Hint text from backend (shown below sentence, always visible if present) */}
      {hintDisplay && !submitted && (
        <p className="text-xs text-[var(--text-muted)] -mt-3 flex items-center gap-1">
          <span aria-hidden="true">💡</span>
          {hintDisplay}
        </p>
      )}

      {/* Input area */}
      {!isWordPool ? (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleTextInput}
            disabled={submitted}
            placeholder={t('exercise.fill_placeholder') || 'Cevabınızı yazın...'}
            className="w-full px-4 py-3 border-2 border-[var(--border-light)] rounded-xl text-[var(--text-main)] focus:border-[var(--brand-primary)] focus:outline-none disabled:bg-gray-50 disabled:cursor-default transition-colors"
            aria-label={t('exercise.fill_input_label') || 'Cevap girişi'}
            aria-describedby={hintUsed ? `${testId}-hint-desc` : undefined}
            data-testid={`${testId}-input`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitted) handleSubmit();
            }}
          />
          {hintUsed && (
            <p id={`${testId}-hint-desc`} className="sr-only">
              {t('exercise.hint_used') || 'İpucu kullanıldı, puan düşük olabilir.'}
            </p>
          )}
        </div>
      ) : (
        /* Word pool pills */
        <div
          className="flex flex-wrap gap-2"
          role="listbox"
          aria-label={t('exercise.word_pool_label') || 'Kelime havuzu'}
          data-testid={`${testId}-word-pool`}
        >
          {currentQ.wordPool.map((word) => {
            const isSelected = inputValue === word;
            return (
              <motion.button
                key={word}
                whileHover={submitted || shouldReduce ? {} : { scale: 1.03 }}
                whileTap={submitted || shouldReduce ? {} : { scale: 0.97 }}
                onClick={() => handleWordSelect(word)}
                disabled={submitted}
                role="option"
                aria-selected={isSelected}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] disabled:cursor-default ${
                  isSelected
                    ? 'border-[var(--brand-primary)] bg-sky-50 text-sky-700'
                    : 'border-[var(--border-light)] bg-white text-[var(--text-main)] hover:border-[var(--brand-primary)]'
                }`}
                data-testid={`${testId}-word-${word}`}
              >
                {word}
              </motion.button>
            );
          })}
        </div>
      )}

      {/* Feedback — only shown after server responds (checking=false) */}
      <AnimatePresence>
        {submitted && !checking && (
          <motion.div
            key="feedback"
            initial={shouldReduce ? {} : { opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              className={`flex items-start gap-3 p-4 rounded-xl border-2 ${
                correct
                  ? 'border-[#10B981] bg-[#10B981]/10'
                  : 'border-[var(--brand-danger)] bg-[var(--brand-danger)]/10'
              }`}
              role="alert"
              data-testid={`${testId}-feedback`}
            >
              {correct ? (
                <CheckCircle size={20} className="text-[#10B981] shrink-0 mt-0.5" aria-hidden="true" />
              ) : (
                <XCircle size={20} className="text-[var(--brand-danger)] shrink-0 mt-0.5" aria-hidden="true" />
              )}
              <div>
                <p className={`text-sm font-bold mb-1 ${correct ? 'text-emerald-700' : 'text-red-700'}`}>
                  {correct
                    ? t('exercise.correct') || 'Doğru!'
                    : t('exercise.wrong') || 'Yanlış!'}
                </p>
                {!correct && (
                  <p className="text-sm text-[var(--text-muted)]">
                    {t('exercise.correct_answer') || 'Doğru cevap:'}{' '}
                    <strong>{displayCorrectAnswer}</strong>
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buttons */}
      <div className="flex items-center justify-between">
        {/* Hint button — only show if no backend hint and not word pool */}
        {!submitted && !isWordPool && !hintDisplay && (
          <button
            onClick={handleHint}
            disabled={hintUsed}
            className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--brand-primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] rounded-lg px-2 py-1"
            data-testid={`${testId}-hint`}
          >
            <HelpCircle size={16} aria-hidden="true" />
            {t('exercise.hint') || 'İpucu'}
          </button>
        )}

        <div className="ml-auto">
          {!submitted ? (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleSubmit}
              disabled={!inputValue.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid={`${testId}-submit`}
            >
              {t('exercise.submit') || 'Kontrol Et'}
            </motion.button>
          ) : checking ? (
            /* Awaiting server grading — neutral loading state */
            <div
              className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-400 font-bold rounded-xl cursor-wait"
              data-testid={`${testId}-checking`}
              aria-busy="true"
              aria-label={t('exercise.checking') || 'Kontrol ediliyor...'}
            >
              <span className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" aria-hidden="true" />
              {t('exercise.checking') || 'Kontrol ediliyor...'}
            </div>
          ) : (
            <motion.button
              whileHover={shouldReduce ? {} : { scale: 1.01 }}
              whileTap={shouldReduce ? {} : { scale: 0.99 }}
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-3 bg-[var(--brand-primary)] text-white font-bold rounded-xl shadow-[0_4px_0_0_var(--brand-primary-shadow)] hover:bg-[var(--brand-primary-hover)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
              data-testid={`${testId}-next`}
            >
              {isLast ? t('exercise.finish') || 'Bitir' : t('exercise.next') || 'Sonraki'}
              <ChevronRight size={18} aria-hidden="true" />
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}
